import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, BookOpen, MapPin, ArrowUpDown, ChevronRight, ChevronLeft, Calendar, BookMarked, Repeat, ArrowRight, Crown, Sparkles, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CAROUSEL_SLIDES = [
  {
    src: '/carousel/logo.jpeg',
    alt: "mybook.rent - India's first peer to peer book rental app",
    href: '#browse-books',
  },
  {
    src: '/carousel/how-it-works.png',
    alt: 'How It Works - Rent books easily',
    href: '#browse-books',
  },
];

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [books, setBooks] = useState([]);
  const [nearbyBooks, setNearbyBooks] = useState([]);
  const [mapPin, setMapPin] = useState('');
  const [homepageAds, setHomepageAds] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || "");
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [pincode, setPincode] = useState("");
  const [pincodeTouched, setPincodeTouched] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({ name: '', message: '', rating: 5 });
  const [testimonials, setTestimonials] = useState([]);
  const [sortBy, setSortBy] = useState('new_arrivals');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const [welcomeBanner, setWelcomeBanner] = useState(() => sessionStorage.getItem('welcomeBanner') || '');
  const BOOKS_PER_PAGE = 10;
  const booksRef = useRef(null);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    fetchCategories();
    fetchTestimonials();
  }, []);

  // Prefill pincode from the logged-in user's profile (not a hardcoded default)
  useEffect(() => {
    if (!pincodeTouched && user?.pincode) {
      setPincode(String(user.pincode).trim());
    }
    if (!user) {
      setPincodeTouched(false);
      setPincode('');
    }
  }, [user?.pincode, user, pincodeTouched]);

  // Debounce search so results don't flash/vanish on every keystroke (MBR-022)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      const next = {};
      if (searchInput) next.q = searchInput;
      if (selectedCategory) next.category = selectedCategory;
      setSearchParams(next);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, selectedCategory, setSearchParams]);

  useEffect(() => {
    const cat = searchParams.get('category') || '';
    const q = searchParams.get('q') || '';
    setSelectedCategory(cat);
    if (q !== searchInput) setSearchInput(q);
  }, [searchParams]);

  useEffect(() => {
    fetchBooks();
  }, [selectedCategory, searchQuery, sortBy]);

  // MBR-090: Near You must use the user's location (or typed pincode), not all cities
  useEffect(() => {
    fetchNearbyBooks();
  }, [user?.pincode, user?.city, pincode]);

  useEffect(() => {
    if (location.hash !== '#browse-books') return;
    const t = setTimeout(() => {
      document.getElementById('browse-books')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [location.hash, loading]);

  useEffect(() => {
    axios.get(`${API}/homepage-ads`).then((res) => {
      setHomepageAds(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setHomepageAds([]));
  }, []);

  const carouselSlides = homepageAds.length
    ? homepageAds.map((ad) => ({
        src: ad.image_url,
        alt: ad.alt || 'Advertisement',
        href: (ad.link_url || '').trim() || '#browse-books',
      }))
    : CAROUSEL_SLIDES;

  const handleSlideClick = (slide) => {
    const rawHref = (slide?.href || '').trim();
    if (!rawHref || rawHref === '#browse-books' || rawHref === '#' || rawHref === '/#browse-books') {
      const el = document.getElementById('browse-books');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        navigate('/#browse-books');
      }
      return;
    }

    if (rawHref.startsWith('/')) {
      navigate(rawHref);
      return;
    }

    let url = rawHref;
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Fade between slides (appear), do not slide
  useEffect(() => {
    const timer = setInterval(() => setCarouselIdx(p => (p + 1) % carouselSlides.length), 4000);
    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  const dismissWelcomeBanner = () => {
    sessionStorage.removeItem('welcomeBanner');
    setWelcomeBanner('');
  };

  // Note: Removed auto-pincode filter - users should see ALL books by default

  // Sort books based on selected sort option
  const sortBooks = (booksToSort) => {
    const sorted = [...booksToSort];
    switch (sortBy) {
      case 'new_arrivals':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'price_low_high':
        return sorted.sort((a, b) => a.rental_price_per_week - b.rental_price_per_week);
      case 'price_high_low':
        return sorted.sort((a, b) => b.rental_price_per_week - a.rental_price_per_week);
      case 'rent':
        return sorted.filter(b => b.listing_type === 'rent' || !b.listing_type);
      case 'sell':
        return sorted.filter(b => b.listing_type === 'sell');
      case 'sale':
        return sorted.filter(b => b.on_sale === true);
      case 'best_rentals':
        return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'customer_reviews':
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      default:
        return sorted;
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories', error);
      setCategories([]);
    }
  };

  const fetchBooks = async (page = 1) => {
    // Avoid blanking the grid on every search keystroke refresh
    if (page === 1 && books.length === 0) setLoading(true);
    else if (page > 1) setLoadingMore(true);
    try {
      const params = { page, limit: BOOKS_PER_PAGE, dedupe: true };
      if (selectedCategory) params.category = selectedCategory;
      // Pincode filters "Books Near You" only — keep the main catalogue platform-wide
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/books`, { params });
      const data = response.data;
      const fetchedBooks = data.books || data;
      const sortedBooks = sortBooks(fetchedBooks);
      
      if (page === 1) {
        setBooks(sortedBooks);
      } else {
        setBooks(prev => [...prev, ...sortedBooks]);
      }
      
      setCurrentPage(data.page || page);
      setTotalPages(data.total_pages || 1);
      setTotalBooks(data.total || fetchedBooks.length);
    } catch (error) {
      console.error('Failed to fetch books', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  /** Platform catalogue near the user's pincode/city (MBR-090). */
  const fetchNearbyBooks = async () => {
    const pin = (pincode || user?.pincode || '').trim();
    const city = (!pin && user?.city) ? String(user.city).trim() : '';
    if (!pin && !city) {
      setNearbyBooks([]);
      return;
    }
    try {
      const params = { page: 1, limit: 100, dedupe: true };
      if (pin) params.pincode = pin;
      else if (city) params.city = city;
      const response = await axios.get(`${API}/books`, { params });
      const data = response.data;
      setNearbyBooks(data.books || data || []);
    } catch (error) {
      console.error('Failed to fetch nearby books', error);
      setNearbyBooks([]);
    }
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      fetchBooks(currentPage + 1);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    if (searchInput) setSearchParams({ q: searchInput });
    else setSearchParams({});
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      const seen = new Set();
      const good = (Array.isArray(response.data) ? response.data : []).filter((t) => {
        const name = String(t?.name || t?.user_name || '').trim();
        const message = String(t?.message || t?.comment || '').trim();
        const rating = Number(t?.rating ?? 5);
        if (rating < 4 || name.length < 2 || message.length < 8) return false;
        const key = message.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        t.name = name;
        t.message = message;
        t.rating = rating;
        return true;
      });
      setTestimonials(good);
    } catch (error) {
      console.error('Failed to fetch testimonials', error);
    }
  };

  const handleSubmitTestimonial = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/testimonials`, testimonialForm);
      toast.success('Thank you for sharing your story!');
      setTestimonialForm({ name: '', message: '', rating: 5 });
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to submit testimonial');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      {welcomeBanner && (
        <div
          className="border-b px-4 py-3"
          style={{ backgroundColor: 'rgba(168, 153, 104, 0.15)', borderColor: 'var(--primary)' }}
          data-testid="welcome-banner"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm sm:text-base font-medium" style={{ color: 'var(--primary)' }}>
              {welcomeBanner}
            </p>
            <button
              type="button"
              onClick={dismissWelcomeBanner}
              className="p-1 rounded hover:bg-black/5"
              aria-label="Dismiss welcome message"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════ CAROUSEL SLIDER ═══════════ */}
      <section className="relative overflow-hidden" style={{backgroundColor: '#F5C518'}}>
        <div className="relative h-[280px] sm:h-[360px] md:h-[420px] max-w-7xl mx-auto">
          {carouselSlides.map((img, i) => (
            <div
              key={i}
              onClick={() => handleSlideClick(img)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSlideClick(img);
                }
              }}
              role="button"
              tabIndex={i === carouselIdx ? 0 : -1}
              aria-label={img.alt || 'Homepage banner advertisement'}
              title={img.href && img.href !== '#browse-books' ? `Open: ${img.href}` : 'Click to explore available books'}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out cursor-pointer flex items-center justify-center select-none"
              style={{
                opacity: i === carouselIdx ? 1 : 0,
                pointerEvents: i === carouselIdx ? 'auto' : 'none',
              }}
              data-testid={`carousel-slide-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-contain transition-transform duration-300 hover:scale-[1.01]"
              />
            </div>
          ))}
          {/* Arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCarouselIdx((p) => (p - 1 + carouselSlides.length) % carouselSlides.length);
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-10 h-10 flex items-center justify-center z-10 transition-colors shadow-md"
            data-testid="carousel-prev"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCarouselIdx((p) => (p + 1) % carouselSlides.length);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-10 h-10 flex items-center justify-center z-10 transition-colors shadow-md"
            data-testid="carousel-next"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
            {carouselSlides.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setCarouselIdx(i);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === carouselIdx ? 'bg-white scale-125 shadow' : 'bg-white/50 hover:bg-white/80'
                }`}
                data-testid={`carousel-dot-${i}`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 pb-2">
        <div className="max-w-3xl mx-auto rounded-2xl border border-[#A89968]/40 bg-[#FBF8F1] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Apply for Scholarships</h2>
            <p className="text-sm text-gray-600 mt-1">Merit-cum-Means support for students who rent books on mybook.rent.</p>
          </div>
          <Button
            onClick={() => navigate('/scholarship/eligibility')}
            className="bg-[#A89968] hover:bg-[#8f8356] text-white"
            data-testid="apply-for-scholarships"
          >
            Apply for Scholarships
          </Button>
        </div>
      </section>

      {/* ═══════════ SEARCH BAR ═══════════ */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search by title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 h-12"
                data-testid="search-input"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                placeholder={user?.pincode ? String(user.pincode) : 'Pincode'}
                value={pincode}
                onChange={(e) => {
                  setPincodeTouched(true);
                  setPincode(e.target.value);
                }}
                className="pl-10 h-12 w-full sm:w-40"
                data-testid="pincode-input"
              />
            </div>
            <Button 
              onClick={handleSearch} 
              className="h-12 px-8 hover:opacity-90"
              style={{backgroundColor: 'var(--accent)', color: 'white'}}
              data-testid="search-button"
            >
              Search
            </Button>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section id="browse-books" className="py-6 px-4 sm:px-6 lg:px-8 scroll-mt-20" ref={booksRef}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <h3 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>
              {selectedCategory ? `${selectedCategory} Books` : 'Available Books'}
              {selectedCategory && <button onClick={() => setSelectedCategory('')} className="ml-3 text-sm font-normal text-gray-500 hover:text-gray-700 underline">Clear filter</button>}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-44" data-testid="sort-dropdown">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_arrivals">New Arrivals</SelectItem>
                  <SelectItem value="price_low_high">Price: Low to High</SelectItem>
                  <SelectItem value="price_high_low">Price: High to Low</SelectItem>
                  <SelectItem value="best_rentals">Best Rentals</SelectItem>
                  <SelectItem value="customer_reviews">Customer Reviews</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="skeleton h-64 rounded-xl"></div>
              ))}
            </div>
          ) : books.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="h-20 w-20 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-600 mb-2">No books found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {books.map((book) => {
                const categoryDiscount = categories.find(c => c.name === book.category)?.discount_percentage || 0;
                const originalPrice = book.rental_price_per_week;
                const discountedPrice = categoryDiscount > 0 
                  ? Math.round(originalPrice * (1 - categoryDiscount / 100))
                  : originalPrice;
                
                return (
                <div
                  key={book.id}
                  onClick={() => navigate(`/books/${book.id}`)}
                  className="book-card bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  data-testid={`book-card-${book.id}`}
                >
                  <div className="relative overflow-hidden" style={{backgroundColor: '#f3ece0', height: '220px'}}>
                    {book.cover_image ? (
                      <img 
                        src={book.cover_image} 
                        alt={book.title} 
                        style={{width: '100%', height: '220px', objectFit: 'cover', display: 'block'}}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }}
                      />
                    ) : null}
                    {!book.cover_image && (
                      <div className="flex items-center justify-center" style={{height: '220px'}}>
                        <BookOpen className="h-14 w-14" style={{color: 'var(--primary)'}} />
                      </div>
                    )}
                    {categoryDiscount > 0 && (
                      <span className="absolute top-2 left-2 bg-red-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">{categoryDiscount}% OFF</span>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="font-bold text-sm mb-0.5 line-clamp-1" style={{color: 'var(--foreground)'}} data-testid={`book-title-${book.id}`}>{book.title}</h4>
                    <p className="text-xs text-gray-500 mb-2 line-clamp-1">{book.author}</p>
                    <div className="flex items-baseline space-x-1">
                      <span className="text-lg font-bold" style={{color: 'var(--accent)'}}>&#8377;{Math.round(discountedPrice)}</span>
                      <span className="text-xs text-gray-400">/week</span>
                    </div>
                    {categoryDiscount > 0 && (
                      <span className="text-xs text-gray-400 line-through">&#8377;{Math.round(originalPrice)}</span>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            
            {currentPage < totalPages && (
              <div className="text-center mt-8">
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  variant="outline"
                  className="px-8 py-3"
                  data-testid="load-more-button"
                >
                  {loadingMore ? 'Loading...' : `Load More (${books.length} of ${totalBooks})`}
                </Button>
              </div>
            )}
            {books.length > 0 && currentPage >= totalPages && (
              <p className="text-center text-gray-400 mt-6 text-sm">Showing all {books.length} books</p>
            )}
            </>
          )}
        </div>
      </section>

      {/* Books Near You — location-filtered (MBR-090) */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-6" style={{color: 'var(--foreground)'}}>Books Near You</h2>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {(pincode || user?.pincode || user?.city) && (
              <div className="mb-6 rounded-xl overflow-hidden border border-gray-200">
                <iframe
                  title="Books near you map"
                  className="w-full h-64 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    mapPin
                      ? `${mapPin}, India`
                      : (pincode || user?.pincode)
                        ? `${String(pincode || user?.pincode).trim()}, India`
                        : `${String(user?.city).trim()}, India`
                  )}&z=12&output=embed`}
                />
                <div className="px-4 py-2 bg-gray-50 text-center">
                  <p className="text-xs text-gray-500 mb-1">
                    Map is pinned to catalogue books near this pincode. Titles below are from the database.
                  </p>
                  {nearbyBooks.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-2 max-h-24 overflow-y-auto">
                      {nearbyBooks.slice(0, 12).map((b) => (
                        <button
                          key={b.id || `${b.title}-${b.pincode}`}
                          type="button"
                          className="text-xs px-2 py-1 rounded-full bg-white border hover:border-amber-500 truncate max-w-[140px]"
                          title={`${b.title} · ${b.pincode || ''}`}
                          onClick={() => {
                            if (b.pincode) setMapPin(String(b.pincode));
                            navigate(`/books/${b.id}`);
                          }}
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  )}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      mapPin
                        ? `${mapPin}, India`
                        : (pincode || user?.pincode)
                          ? `${String(pincode || user?.pincode).trim()}, India`
                          : `${String(user?.city).trim()}, India`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium hover:underline"
                    style={{ color: 'var(--primary)' }}
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            )}
            {!(pincode || user?.pincode || user?.city) ? (
              <div className="text-center py-8">
                <MapPin className="h-10 w-10 mx-auto mb-3" style={{color: 'var(--primary)'}} />
                <p className="font-medium text-gray-800">Enter your pincode above to see books near you</p>
                <p className="text-sm text-gray-500 mt-1">
                  {user ? 'Or add a pincode to your profile for automatic nearby results.' : 'Log in with a profile pincode for automatic nearby results.'}
                </p>
              </div>
            ) : nearbyBooks.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-700 font-medium">No books found near {pincode || user?.pincode || user?.city}</p>
                <p className="text-sm text-gray-500 mt-1">Try another pincode, or browse the full catalogue above.</p>
              </div>
            ) : (
              <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {Object.entries(
                nearbyBooks.reduce((acc, book) => {
                  const code = book.pincode || book.city || 'Nearby';
                  if (!acc[code]) acc[code] = [];
                  acc[code].push(book);
                  return acc;
                }, {})
              ).slice(0, 6).map(([pincodeArea, pincodeBooks]) => (
                <div 
                  key={pincodeArea} 
                  className="p-4 rounded-xl border-2 hover:shadow-lg transition-shadow cursor-pointer"
                  style={{borderColor: pincode === pincodeArea ? 'var(--primary)' : '#e5e7eb'}}
                  onClick={() => {
                    setPincodeTouched(true);
                    setPincode(pincodeArea);
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-5 w-5" style={{color: 'var(--primary)'}} />
                      <span className="font-bold" style={{color: 'var(--foreground)'}}>{pincodeArea}</span>
                    </div>
                    <span className="text-sm px-2 py-1 rounded-full" style={{backgroundColor: 'var(--secondary)', color: 'var(--primary)'}}>
                      {pincodeBooks.length} {pincodeBooks.length === 1 ? 'book' : 'books'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="text-xs underline mt-1"
                    style={{ color: 'var(--primary)' }}
                    onClick={(e) => { e.stopPropagation(); setMapPin(pincodeArea); }}
                  >
                    Show these books on the map
                  </button>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pincodeBooks.slice(0, 3).map((b, i) => (
                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded truncate max-w-[100px]">
                        {b.title}
                      </span>
                    ))}
                    {pincodeBooks.length > 3 && (
                      <span className="text-xs text-gray-500">+{pincodeBooks.length - 3} more</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 p-4 rounded-xl" style={{backgroundColor: 'var(--card)'}}>
              <div className="text-center">
                <p className="text-3xl font-bold" style={{color: 'var(--primary)'}}>{nearbyBooks.length}</p>
                <p className="text-sm text-gray-600">Total Books</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold" style={{color: 'var(--accent)'}}>
                  {new Set(nearbyBooks.map(b => b.pincode).filter(Boolean)).size}
                </p>
                <p className="text-sm text-gray-600">Locations</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold" style={{color: 'var(--primary)'}}>
                  {nearbyBooks.filter(b => b.is_available).length}
                </p>
                <p className="text-sm text-gray-600">Available Now</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mt-4 text-center">
              Showing books near {pincode || user?.pincode || user?.city}. Change the pincode above to explore another area.
            </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Share Your Story */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center" style={{color: 'var(--foreground)'}}>Share Your Story</h2>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <form onSubmit={handleSubmitTestimonial} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--foreground)'}}>Your Name</label>
                <Input
                  value={testimonialForm.name}
                  onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})}
                  placeholder="Enter your name"
                  required
                  data-testid="testimonial-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--foreground)'}}>Your Experience</label>
                <textarea
                  className="w-full p-3 border rounded-lg"
                  rows="4"
                  value={testimonialForm.message}
                  onChange={(e) => setTestimonialForm({...testimonialForm, message: e.target.value})}
                  placeholder="Share your experience with RentMyBook..."
                  required
                  data-testid="testimonial-message-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: 'var(--foreground)'}}>Rating</label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setTestimonialForm({...testimonialForm, rating: star})}
                      className="text-3xl"
                      style={{color: star <= testimonialForm.rating ? '#FFD700' : '#D1D5DB'}}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 hover:opacity-90"
                style={{backgroundColor: 'var(--accent)', color: 'white'}}
                data-testid="submit-testimonial-button"
              >
                Submit Your Story
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* What Readers Say - Rolling Testimonials */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{color: 'var(--foreground)'}}>What Readers Say</h2>
          <div className="relative">
            <div className="flex animate-scroll-testimonials">
              {/* First set */}
              {testimonials.map((testimonial, index) => (
                <div
                  key={`first-${testimonial.id}-${index}`}
                  className="flex-shrink-0 w-80 mx-3 bg-white rounded-2xl shadow-md p-6"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-xl" style={{color: i < testimonial.rating ? '#FFD700' : '#D1D5DB'}}>
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic line-clamp-5">"{testimonial.message}"</p>
                  <p className="font-semibold" style={{color: 'var(--primary)'}}>- {testimonial.name}</p>
                </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {testimonials.map((testimonial, index) => (
                <div
                  key={`second-${testimonial.id}-${index}`}
                  className="flex-shrink-0 w-80 mx-3 bg-white rounded-2xl shadow-md p-6"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-xl" style={{color: i < testimonial.rating ? '#FFD700' : '#D1D5DB'}}>
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic line-clamp-5">"{testimonial.message}"</p>
                  <p className="font-semibold" style={{color: 'var(--primary)'}}>- {testimonial.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <style>{`
        @keyframes scroll-testimonials {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-testimonials {
          animation: scroll-testimonials 25s linear infinite;
          width: max-content;
        }
        .animate-scroll-testimonials:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
